import { DataViews } from '../DataViews.js';

/**
 * Returns the height needed to clear the clearance requirements of the item passed in and the other item to clear passed in
 * @param {Object} item - the marker object that we are checking clearance for
 * @param {Object} itemToClear - the other marker object that we are trying to avoid
 * @param {String} itemToClearType - the marker type of the itemToClear
 * @param {String} entityType - the type of entity this is running on (node or section)
 * @param {Object} traces - the traces object from Firebase
 * @param {Object} clearanceRules - the clearance rules from the job models
 * @returns {Number|Null} the height to satisfy the clearance requirements, or null if no rule can be determined
 */
export function GetClearanceHeight(item, itemToClear, itemToClearType, entityType, traces, clearanceRules) {
  let itemClearanceRule = GetClearanceRuleForMarker(item, item._marker_context?.type, clearanceRules, traces);
  if (itemClearanceRule) {
    // Check if we found an item to check clearance against
    if (itemToClear) {
      // Find the clearance rule for the clearanceItem (pass in the measuredItemType to indicate 'equipment' vs 'wire')
      let itemToClearClearance = GetClearanceRuleForMarker(itemToClear, itemToClear._marker_context?.type, clearanceRules, traces);
      if (itemToClearClearance) {
        // Set the type of clearances to check based on if the photo is a node or midspan photo
        let clearanceType = entityType == 'node' ? 'clearances' : 'midspan_clearances';
        // Check that the proposed cable has a clearance rule with the item
        if (itemClearanceRule?.[clearanceType]?.[itemToClearClearance.$key]) {
          // Only use the clearance if it's not the ground catch all clearance
          if (itemClearanceRule[clearanceType][itemToClearClearance.$key].clearance < 9999) {
            // Converts the clearance distance to negative if we need to place below
            let multiplier = 1;
            // If the direction is below, then the multiplier should be negative
            if (itemClearanceRule[clearanceType][itemToClearClearance.$key].direction == 'below') multiplier = -1;
            // If the direction is "away from", then only make it negative if the itemToClearType is power
            if (itemClearanceRule[clearanceType][itemToClearClearance.$key].direction == 'away from' && itemToClearType == 'power')
              multiplier = -1;
            // Adjust the height of the proposed accordingly
            return (
              (DataViews.help.getProposedMarkerHeight(itemToClear, false, true) || itemToClear?.height || null) +
              itemClearanceRule[clearanceType][itemToClearClearance.$key].clearance * multiplier
            );
          }
        }
      }
    }
  }
  return null;
}

/**
 * Finds the first clearance rule that matches for the given marker
 * @param {Object} markerData - a marker object
 * @param {String} markerType - the type of marker
 * @param {Object} clearanceRules - the clearance rules from the job models
 * @param {Object} traces - the traces object from Firebase
 * @returns {Object} the first clearance rule that matches the marker passed in based on attribute and trace rules
 */
export function GetClearanceRuleForMarker(markerData, markerType, clearanceRules, traces) {
  let matchingModel = undefined;

  if (!markerData) return matchingModel;

  let traceData = traces?.[markerData._trace];
  if (clearanceRules) {
    modelCheck: for (let i = 0; i < clearanceRules.length; i++) {
      let model = clearanceRules[i];
      if (model.marker_type == markerType) {
        // Try to use the new marker rules.
        if (model.marker_rules) {
          let markerRulesPass = CheckMRRules(model.marker_rules, (attr, val) => markerData[attr] == val);
          if (!markerRulesPass) continue modelCheck;
        }
        // If they are missing, try to use the old format.
        else if (model.attributes) {
          for (let attr in model.attributes) {
            if (markerData[attr] != model.attributes[attr]) {
              continue modelCheck;
            }
          }
        }
        // Try to use the new trace rules.
        if (model.trace_rules) {
          let traceRulesPass = CheckMRRules(model.trace_rules, (attr, val) => traceData?.[attr] == val);
          if (!traceRulesPass) continue modelCheck;
        }
        // If they are missing, try to use the old format.
        else if (model.trace_attributes) {
          for (let attr in model.trace_attributes) {
            if (traceData?.[attr] != model.trace_attributes[attr]) {
              continue modelCheck;
            }
          }
        }
        //Passed all the checks!
        matchingModel = model;
        break;
      }
    }
  }
  return matchingModel;
}

/**
 * A function to recursively check the marker_rules or trace_rules on a clearance rule.
 * @param {Object} group - the group of rules to check
 * @param {Object} callback - the callback function to check for each rule applied
 * @returns
 */
function CheckMRRules(group, callback) {
  // Return if group is null.
  if (group == null) return;
  // Default operator to 'all'.
  group.operator = group.operator || 'all';
  let any = false;
  let all = true;
  for (let key in group.attributes) {
    let val = group.attributes[key];
    let pass = key.startsWith('group') ? CheckMRRules(val, callback) : callback(key, val);
    if (pass) any = true;
    else all = false;
    if (group.operator == 'all' && !all) return all;
    if (group.operator == 'any' && any) return any;
  }
  if (group.operator == 'all') return all;
  if (group.operator == 'any') return any;
}
